import React, { useState, useEffect } from 'react';
import { usuariosAPI, clientesAPI, instancesAPI, monitoringAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import { 
  Users, 
  Building, 
  Shield, 
  Activity, 
  Server, 
  Phone, 
  Bot, 
  MessageSquare, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Cpu,
  HardDrive,
  Zap,
  Settings,
  Power,
  RotateCcw
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { PageSkeleton, SkeletonMetricCard, SkeletonTabela } from '../components/Skeleton';

const AdminPanel = () => {
  const { user } = useAuth();
  const [funil, setFunil] = useState(null);
  const [trialClientes, setTrialClientes] = useState([]);
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState(null);
  const [instances, setInstances] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [browserStatus, setBrowserStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAllData();
    loadFunil();
    const interval = setInterval(loadAllData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadFunil = async () => {
    try {
      const [f, t] = await Promise.all([
        api.get('/dashboard/admin/funil-trial?dias=30'),
        api.get('/dashboard/admin/clientes-trial'),
      ]);
      setFunil(f.data);
      setTrialClientes(t.data || []);
    } catch {}
  };

  const loadAllData = async () => {
    try {
      const [usersRes, clientsRes, healthRes, browserRes] = await Promise.all([
        usuariosAPI.list().catch(() => ({ data: { usuarios: [] } })),
        clientesAPI.list().catch(() => ({ data: [] })),
        monitoringAPI.getSystemHealth().catch(() => ({ data: null })),
        instancesAPI.getBrowserStatus().catch(() => ({ data: null }))
      ]);
      
      setUsers(usersRes.data.usuarios || []);
      setClients(clientsRes.data || []);
      setSystemHealth(healthRes.data);
      setBrowserStatus(browserRes.data);
      
      // Carregar instâncias para cada cliente
      const instancePromises = (clientsRes.data || []).map(async (client) => {
        try {
          const instanceRes = await instancesAPI.getStatusReal(client.id);
          return {
            cliente: client,
            instance: instanceRes.data?.data || instanceRes.data,
            status: instanceRes.data?.success ? 'success' : 'error'
          };
        } catch (error) {
          return {
            cliente: client,
            instance: null,
            status: 'error',
            error: error.message
          };
        }
      });
      
      const instancesData = await Promise.all(instancePromises);
      setInstances(instancesData);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do sistema');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
  };

  const getInstanceStatusColor = (instanceData) => {
    if (!instanceData.instance) return 'bg-red-500';
    if (instanceData.instance.status === 'conectada') return 'bg-green-500';
    return 'bg-yellow-500';
  };

  const getInstanceStatusText = (instanceData) => {
    if (!instanceData.instance) return 'Não configurado';
    if (instanceData.instance.status === 'conectada') return 'Online';
    return 'Offline';
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    if (!seconds) return '0s';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Componente para cada cliente
  const ClientInstanceCard = ({ instanceData, darkMode, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const handleAction = async (action, clienteId) => {
      setLoading(true);
      try {
        let response;
        switch (action) {
          case 'initialize':
            response = await instancesAPI.initializeClient(clienteId);
            break;
          case 'disconnect':
            response = await instancesAPI.disconnectAPI(clienteId);
            break;
          case 'restart':
            response = await instancesAPI.restart(clienteId);
            break;
          case 'reset':
            response = await instancesAPI.resetInstance(clienteId);
            break;
          case 'reconnect':
            response = await instancesAPI.reconnectInstance(clienteId);
            break;
          case 'clearSession':
            response = await instancesAPI.clearSession(clienteId);
            break;
          case 'activateIA':
            response = await instancesAPI.activateIA(clienteId);
            break;
          case 'deactivateIA':
            response = await instancesAPI.deactivateIA(clienteId);
            break;
          case 'toggleListener':
            response = await instancesAPI.toggleFeature(clienteId, 'listener', 'toggle');
            break;
          case 'diagnostico':
            response = await instancesAPI.diagnostico(clienteId);
            toast.info(`Diagnóstico: ${response.data?.message || 'Executado'}`);
            break;
          case 'autoFix':
            response = await instancesAPI.corrigirAutomatico(clienteId);
            break;
          case 'checkListener':
            response = await instancesAPI.verificarListener(clienteId);
            toast.info(`Listener: ${response.data?.ativo ? 'Ativo' : 'Inativo'} (${response.data?.listeners || 0})`);
            break;
          case 'checkBrowser':
            response = await instancesAPI.verificarBrowser();
            toast.info(`Browser: ${response.data?.status || 'Desconhecido'} (${response.data?.sessions || 0} sessões)`);
            break;
          default:
            return;
        }
        
        if (response?.data?.success !== false) {
          toast.success(`Ação ${action} executada com sucesso!`);
        } else {
          toast.warning(response?.data?.message || 'Ação executada com avisos');
        }
        
        setTimeout(onRefresh, 1000);
      } catch (error) {
        toast.error(`Erro ao executar ${action}: ${error.response?.data?.message || error.message}`);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className={`border rounded-lg p-4 ${
        darkMode ? 'border-gray-600 bg-zinc-700' : 'border-gray-200 bg-gray-50'
      }`}>
        {/* Header do Cliente */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className={`w-4 h-4 rounded-full ${getInstanceStatusColor(instanceData)}`}></div>
            <div>
              <h4 className={`font-semibold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>{instanceData.cliente.nome}</h4>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {instanceData.instance?.numero_whatsapp || 'Número não configurado'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 text-xs rounded-full ${
              instanceData.instance?.status === 'conectada'
                ? 'bg-green-100 text-green-800'
                : instanceData.instance
                ? 'bg-red-100 text-red-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {getInstanceStatusText(instanceData)}
            </span>
            <button
              onClick={() => setExpanded(!expanded)}
              className={`p-1 rounded ${
                darkMode ? 'hover:bg-zinc-600' : 'hover:bg-gray-200'
              }`}
            >
              {expanded ? '▼' : '▶'}
            </button>
          </div>
        </div>

        {/* Status Indicators */}
        <div className="flex items-center space-x-4 mb-4">
          <div className="flex items-center space-x-1">
            <Bot className={`h-4 w-4 ${
              instanceData.instance?.ia_config_id ? 'text-green-500' : 'text-gray-400'
            }`} />
            <span className={`text-xs ${
              instanceData.instance?.ia_config_id ? 'text-green-600' : 'text-gray-500'
            }`}>IA</span>
          </div>
          <div className="flex items-center space-x-1">
            <MessageSquare className={`h-4 w-4 ${
              instanceData.instance?.listener?.ativo ? 'text-blue-500' : 'text-gray-400'
            }`} />
            <span className={`text-xs ${
              instanceData.instance?.listener?.ativo ? 'text-blue-600' : 'text-gray-500'
            }`}>Auto</span>
          </div>
        </div>

        {/* Ações Expandidas */}
        {expanded && (
          <div className="space-y-4 border-t pt-4">
            {/* Controles de Conexão */}
            <div>
              <h5 className={`text-sm font-medium mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Controles de Conexão</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  onClick={() => handleAction('initialize', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Inicializar
                </button>
                <button
                  onClick={() => handleAction('disconnect', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Desconectar
                </button>
                <button
                  onClick={() => handleAction('restart', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                >
                  Reiniciar
                </button>
                <button
                  onClick={() => handleAction('clearSession', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Limpar Sessão
                </button>

              </div>
            </div>

            {/* Controles de Funcionalidades */}
            <div>
              <h5 className={`text-sm font-medium mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Funcionalidades</h5>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleAction('activateIA', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Ativar IA
                </button>
                <button
                  onClick={() => handleAction('deactivateIA', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Desativar IA
                </button>
                <button
                  onClick={() => handleAction('toggleListener', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Toggle Listener
                </button>
              </div>
            </div>

            {/* Diagnóstico */}
            <div>
              <h5 className={`text-sm font-medium mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Diagnóstico e Correção</h5>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleAction('diagnostico', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  Diagnóstico
                </button>
                <button
                  onClick={() => handleAction('autoFix', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  Correção Auto
                </button>
                <button
                  onClick={() => handleAction('checkListener', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Check Listener
                </button>
                <button
                  onClick={() => handleAction('checkBrowser', instanceData.cliente.id)}
                  disabled={loading}
                  className="px-3 py-2 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                >
                  Check Browser
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Painel Administrativo" subtitle="Visão geral da plataforma e monitoramento do sistema">
          <><div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <SkeletonMetricCard key={i} />)}</div><SkeletonTabela colunas={5} linhas={6} /></>
        </PageSkeleton>
      </Layout>
    );
  }

  const connectedInstances = instances.filter(i => i.instance?.status === 'conectada').length;
  const totalInstances = instances.length;
  const activeUsers = users.filter(u => u.status).length;
  const activeClients = clients.filter(c => c.status === 1).length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`text-2xl font-bold flex items-center ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Shield className="mr-2" />
              Painel Administrativo
            </h1>
            <p className={`mt-1 text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Visão geral da plataforma e monitoramento do sistema
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="gradient-button px-4 py-2 rounded flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>

        {/* Funil Trial */}
        {funil && (
          <div className={`rounded-2xl p-5 ${darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
            <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>📊 Funil de Conversão (últimos {funil.periodo_dias} dias)</h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {[
                { label: 'Registros', valor: funil.registros, taxa: null, cor: 'blue' },
                { label: 'Email confirmado', valor: funil.email_confirmado, taxa: funil.taxas.email, cor: 'cyan' },
                { label: 'Cadastro completo', valor: funil.cadastro_completo, taxa: funil.taxas.cadastro, cor: 'indigo' },
                { label: 'Canal conectado', valor: funil.canal_conectado, taxa: funil.taxas.canal, cor: 'purple' },
                { label: 'Contatos importados', valor: funil.contatos_importados, taxa: funil.taxas.contatos, cor: 'amber' },
                { label: 'Upgrades (pagou)', valor: funil.upgrades, taxa: funil.taxas.conversao, cor: 'emerald' },
              ].map((item, i) => (
                <div key={i} className={`rounded-xl p-3 text-center ${darkMode ? 'bg-zinc-700/50' : 'bg-gray-50'}`}>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.valor}</p>
                  <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{item.label}</p>
                  {item.taxa !== null && <p className={`text-xs font-medium mt-1 ${item.taxa >= 50 ? 'text-emerald-500' : item.taxa >= 20 ? 'text-amber-500' : 'text-red-500'}`}>{item.taxa}%</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabela Clientes Trial */}
        {trialClientes.length > 0 && (
          <div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
            <div className="p-4">
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>👥 Clientes Trial ({trialClientes.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={darkMode ? 'bg-zinc-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-4 py-2 text-left ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Nome</th>
                    <th className={`px-4 py-2 text-left ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Email</th>
                    <th className={`px-4 py-2 text-center ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Dias</th>
                    <th className={`px-4 py-2 text-center ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Email ✓</th>
                    <th className={`px-4 py-2 text-center ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>CPF</th>
                    <th className={`px-4 py-2 text-center ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Endereço</th>
                    <th className={`px-4 py-2 text-center ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Canais</th>
                    <th className={`px-4 py-2 text-center ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Contatos</th>
                    <th className={`px-4 py-2 text-center ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trialClientes.map(c => (
                    <tr key={c.id} className={`border-t ${darkMode ? 'border-zinc-700 hover:bg-zinc-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className={`px-4 py-2.5 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.nome}</td>
                      <td className={`px-4 py-2.5 ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{c.email}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          c.dias_restantes <= 2 ? 'bg-red-500/10 text-red-500' : c.dias_restantes <= 4 ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'
                        }`}>{c.dias_restantes}d</span>
                      </td>
                      <td className="px-4 py-2.5 text-center">{c.email_confirmado ? '✅' : '❌'}</td>
                      <td className="px-4 py-2.5 text-center">{c.tem_cpf ? '✅' : '❌'}</td>
                      <td className="px-4 py-2.5 text-center">{c.tem_endereco ? '✅' : '❌'}</td>
                      <td className="px-4 py-2.5 text-center">{c.canais_conectados || 0}</td>
                      <td className="px-4 py-2.5 text-center">{c.total_contatos || 0}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          c.status === 1 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
                        }`}>{c.status === 1 ? 'Ativo' : 'Suspenso'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* System Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Instâncias WhatsApp */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                <Phone className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Instâncias WhatsApp</p>
                <p className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>{connectedInstances}/{totalInstances}</p>
                <p className="text-xs text-green-600">Conectadas/Total</p>
              </div>
            </div>
          </div>

          {/* Usuários Ativos */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Usuários Ativos</p>
                <p className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>{activeUsers}/{users.length}</p>
                <p className="text-xs text-blue-600">Ativos/Total</p>
              </div>
            </div>
          </div>

          {/* Clientes Ativos */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                <Building className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Clientes Ativos</p>
                <p className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>{activeClients}/{clients.length}</p>
                <p className="text-xs text-purple-600">Ativos/Total</p>
              </div>
            </div>
          </div>

          {/* Status do Sistema */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center">
              <div className={`flex-shrink-0 rounded-md p-3 ${
                systemHealth ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <Server className={`h-6 w-6 ${
                  systemHealth ? 'text-green-600' : 'text-red-600'
                }`} />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Sistema</p>
                <p className={`text-lg font-bold ${
                  systemHealth ? 'text-green-600' : 'text-red-600'
                }`}>
                  {systemHealth ? 'Online' : 'Offline'}
                </p>
                <p className="text-xs text-gray-500">
                  {systemHealth ? 'Operacional' : 'Verificar logs'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* System Health Details */}
        {systemHealth && (
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Activity className="mr-2 h-5 w-5 text-green-600" />
              Saúde do Sistema
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* CPU */}
              <div className="flex items-center space-x-3">
                <Cpu className="h-8 w-8 text-blue-500" />
                <div>
                  <p className={`font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>CPU</p>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {systemHealth.cpu ? `${systemHealth.cpu.usage}%` : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Uptime: {systemHealth.uptime ? formatUptime(systemHealth.uptime) : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Memória */}
              <div className="flex items-center space-x-3">
                <HardDrive className="h-8 w-8 text-orange-500" />
                <div>
                  <p className={`font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>Memória</p>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {systemHealth.memory ? 
                      `${formatBytes(systemHealth.memory.used)}/${formatBytes(systemHealth.memory.total)}` : 
                      'N/A'
                    }
                  </p>
                  <p className="text-xs text-gray-500">
                    {systemHealth.memory ? 
                      `${Math.round((systemHealth.memory.used / systemHealth.memory.total) * 100)}% usado` : 
                      'N/A'
                    }
                  </p>
                </div>
              </div>

              {/* Browser Status */}
              <div className="flex items-center space-x-3">
                <Bot className="h-8 w-8 text-purple-500" />
                <div>
                  <p className={`font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>Browser</p>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {browserStatus?.status === 'online' ? 'Online' : 'Offline'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {browserStatus?.sessions || 0} sessões ativas
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instances Management */}
        <div className={`rounded-lg p-6 ${
          darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 flex items-center ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <Phone className="mr-2 h-5 w-5 text-green-600" />
            Gerenciamento de Instâncias por Cliente
          </h3>
          <div className="space-y-6">
            {instances.map((instanceData, index) => (
              <ClientInstanceCard 
                key={index} 
                instanceData={instanceData} 
                darkMode={darkMode}
                onRefresh={loadAllData}
              />
            ))}
            {instances.length === 0 && (
              <div className={`text-center py-8 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <Phone className="mx-auto h-12 w-12 mb-4 opacity-50" />
                <p>Nenhuma instância configurada</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className={`rounded-lg p-6 ${
          darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 flex items-center ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <Zap className="mr-2 h-5 w-5 text-green-600" />
            Ações Rápidas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => window.location.href = '/admin/users'}
              className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-blue-500 hover:bg-zinc-700'
                  : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}
            >
              <Users className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <p className={`font-medium ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Gerenciar Usuários</p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>{users.length} usuários</p>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/clients'}
              className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-purple-500 hover:bg-zinc-700'
                  : 'border-gray-300 hover:border-purple-500 hover:bg-purple-50'
              }`}
            >
              <Building className="h-8 w-8 mx-auto mb-2 text-purple-500" />
              <p className={`font-medium ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Gerenciar Clientes</p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>{clients.length} clientes</p>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/management'}
              className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-orange-500 hover:bg-zinc-700'
                  : 'border-gray-300 hover:border-orange-500 hover:bg-orange-50'
              }`}
            >
              <Zap className="h-8 w-8 mx-auto mb-2 text-orange-500" />
              <p className={`font-medium ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Ferramentas Avançadas</p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Gerenciamento do sistema</p>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/z-api'}
              className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-nomoz-orange hover:bg-zinc-700'
                  : 'border-gray-300 hover:border-nomoz-orange hover:bg-green-50'
              }`}
            >
              <Server className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p className={`font-medium ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Pool Z-API</p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Gerenciar instâncias Z-API</p>
            </button>

            <button
              onClick={() => window.location.href = '/admin/vendedores'}
              className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-pink-500 hover:bg-zinc-700'
                  : 'border-gray-300 hover:border-pink-500 hover:bg-pink-50'
              }`}
            >
              <Users className="h-8 w-8 mx-auto mb-2 text-pink-500" />
              <p className={`font-medium ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Vendedores</p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Links e comissões</p>
            </button>

            <button
              onClick={() => window.location.href = '/admin/leads'}
              className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-teal-500 hover:bg-zinc-700'
                  : 'border-gray-300 hover:border-teal-500 hover:bg-teal-50'
              }`}
            >
              <Users className="h-8 w-8 mx-auto mb-2 text-teal-500" />
              <p className={`font-medium ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Leads</p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Origem e atribuição</p>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminPanel;